import type { ReactNode } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { Button, Spinner } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';

export interface MucDoan {
  id: string;
  ten: string;
}

/**
 * Cột trái của hai màn hỏi–đáp: nút đoạn mới, danh sách đoạn gần đây, dòng giải thích phạm vi lịch sử.
 *
 * Nút "Đoạn chat mới" chỉ DỌN MÀN HÌNH, không gọi máy chủ: đoạn được tạo lúc gửi câu đầu, nên chỉ ra đời khi có nội dung
 * để đặt tên. Tạo ngay lúc bấm thì mỗi lần đổi ý để lại một dòng không tên vĩnh viễn trong lịch sử.
 */
export function DanhSachDoan({
  nhanMoi, moiDuoc, onMoi, ds, dangChon, dangTai, rong, onChon, onXoa, nhanXoa, chan,
}: {
  nhanMoi: string;
  moiDuoc: boolean;
  onMoi: () => void;
  ds: MucDoan[] | undefined;
  dangChon: string | null;
  dangTai: boolean;
  /** Nội dung khi không có đoạn nào (hoặc khi chưa thể có — chưa có nguồn, tắt lưu lịch sử, lỗi). */
  rong: ReactNode;
  onChon: (id: string) => void;
  onXoa: (m: MucDoan) => void;
  nhanXoa: (ten: string) => string;
  chan: string;
}) {
  const { t } = useT();
  return (
    <aside className="hidden w-64 shrink-0 flex-col overflow-hidden rounded-lg border border-default bg-surface shadow-sm md:flex">
      <div className="flex h-14 shrink-0 items-center border-b border-default px-3">
        <Button size="sm" variant="secondary" className="w-full font-medium" icon={<PlusOutlined />} disabled={!moiDuoc} onClick={onMoi}>
          {nhanMoi}
        </Button>
      </div>

      <div className="px-3 pb-1 pt-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-content-muted">{t('troLyAiHoi.ganDay')}</p>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-3">
        {dangTai ? (
          <div className="mt-4 flex justify-center">
            <Spinner size="small" />
          </div>
        ) : ds?.length ? (
          <ul aria-label={t('troLyAiHoi.danhSachDoan')} className="space-y-0.5">
            {ds.map((m) => {
              const chon = m.id === dangChon;
              return (
                <li
                  key={m.id}
                  className={cn(
                    'group flex items-center gap-1.5 rounded px-1 transition',
                    chon ? 'bg-primary-50 font-semibold text-primary-700' : 'text-content-secondary hover:bg-subtle',
                  )}
                >
                  <button
                    type="button"
                    aria-current={chon ? 'true' : undefined}
                    className="min-w-0 flex-1 truncate px-1.5 py-2 text-start text-xs"
                    onClick={() => onChon(m.id)}
                  >
                    {m.ten}
                  </button>
                  <button
                    type="button"
                    aria-label={nhanXoa(m.ten)}
                    className="shrink-0 rounded p-1 text-content-muted opacity-0 transition hover:bg-danger-50 hover:text-danger-700 focus-visible:opacity-100 group-hover:opacity-100"
                    onClick={() => onXoa(m)}
                  >
                    <DeleteOutlined className="text-xs" />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="px-2 py-4 text-xs leading-relaxed text-content-muted">{rong}</div>
        )}
      </div>

      <p className="border-t border-default px-3 py-2.5 text-xs leading-relaxed text-content-muted">{chan}</p>
    </aside>
  );
}
